import * as React from 'react'
import { styled } from '@mui/material/styles'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import useFaqStyles from './faq.styles'
import { Button } from '@material-ui/core'

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    '&(:last-child)': {
        borderTop: 0
    },
    '&:before': {
        display: 'none'
    }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem', color: 'var(--teal-blue)' }} />} {...props} />
))(({ theme }) => ({
    padding: '1rem',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)'
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1)
    }
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(3)
}))

export const Faq = (props) => {
    const classes = useFaqStyles()

    return (
        <div className={classes.faqWrapper}>
            <div className={classes.faqBlock}>
                <div className={classes.title}>FAQs</div>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel2d-content" id="panel2d-header">
                        <p className={classes.faqText}>
                            What information will First Citizens Bank have access to if I connect my accounting?
                        </p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            First Citizens Bank will have read-only access to your Balance Sheet, Income Statement and Chart of Accounts
                            which enables First Citizens Bank to automate your financial reporting.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel1d-content" id="panel1d-header">
                        <p className={classes.faqText}>
                            Why is it beneficial for me to connect my accounting software to my First Citizens Bank account?
                        </p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            Time saver! You no longer need to download, compile, and email us your financials and potentially may no longer
                            be required to send a separate compliance certificate. Instead, through your accounting software connection,
                            First Citizens Bank will be able to automatically pull the required information when required.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel3d-content" id="panel3d-header">
                        <p className={classes.faqText}>Will I be able to disconnect my accounting connection? </p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            Yes. You will be able to discontinue the connection between First Citizens Bank and your accounting software at
                            any point in time. Please note that disconnecting the connection does not remove the need to share financials.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel4d-content" id="panel4d-header">
                        <p className={classes.faqText}>How do I disconnect my accounting software from First Citizens Bank’s systems?</p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            Contact your Relationship Manager to notify them that you’d like to discontinue your accounting software
                            connection and they’ll be able to process your request.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel4d-content" id="panel4d-header">
                        <p className={classes.faqText}>When will my financials get pulled?</p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            30 days after the end of the previous month for which financials are required. For example, we will pull your
                            financials for the month of February, on March 30.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel4d-content" id="panel4d-header">
                        <p className={classes.faqText}>What if I don’t have my books closed within 30 days after my month-end close?</p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            Contact your Relationship Manager to notify them. They will ensure your financials do not get pulled until they
                            are ready.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel5d-content" id="panel5d-header">
                        <p className={classes.faqText}>What if I need to make changes after I’ve already setup my accounting connection?</p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            If you ever need to connect a different account, or add an additional account, reach out to your Relationship
                            Manager and they’ll help facilitate any changes you need to make.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel5d-content" id="panel5d-header">
                        <p className={classes.faqText}>
                            Why am I not receiving an email after entering my email to start or resume my connection setup?
                        </p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            If you have not received an email within a few minutes, it may be due to an unexpected delay on our end. Please
                            contact your Relationship Manager so we can resolve this promptly.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
                <Accordion className={classes.accordionBlock}>
                    <AccordionSummary className={classes.accordionSummary} aria-controls="panel6d-content" id="panel6d-header">
                        <p className={classes.faqText}>What if the refresh process happens and I need to restate my financials?</p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>
                            No need to worry, you can reach out to your Relationship Manager, and we can pull the updated financials after
                            you have completed your restatement.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </div>
            <Button className={classes.getStartedBtn} data-testid="get-started-btn" onClick={props.getStartedClicked}>
                Get Started
            </Button>
        </div>
    )
}
